import { PurchaseOrderReceiptDispositionStatus } from '@supply-mover-2-0/shared';
import type { ListBuilderConfiguration } from '../types';
import {
	PURCHASE_ORDER_RECEIPTS_MAIN_COLUMNS,
	PURCHASE_ORDER_RECEIPTS_MAIN_REQUIRED_FIELDS,
} from './purchase_order_receipts_main.columns';

/**
 * Tenant-wide Purchase Order Receipts list configuration. Powers the
 * `Inventory → Receipts` page (Phase 3.4).
 *
 * Distinct from `PURCHASE_ORDER_RECEIPT_LIST_CONFIG` (listKey
 * `purchase_order_receipt`) which serves the embedded per-PO Receipts
 * tab — that view passes its own queryFn and uses a smaller column
 * set.
 *
 * Phase 3 ships 3 tabs (Recent / Open Issues / All Receipts). The
 * "My Open" tab lands in Phase 6 once the exception entity surface
 * exists (the assignee filter joins through receipt → detail →
 * exception which is meaningless until exceptions are creatable).
 */
export const PURCHASE_ORDER_RECEIPTS_MAIN_LIST_CONFIG: ListBuilderConfiguration =
	{
		lists: [
			{
				id: 'default-purchase-order-receipts-main-recent',
				cacheTTL: 300000,
				name: 'Recent',
				type: 'default',
				active: true,
				columns: PURCHASE_ORDER_RECEIPTS_MAIN_COLUMNS,
				description:
					'Receipts from the last 30 days. Default landing tab — answers "what came in this week?" for receiving and purchasing.',
				defaultSortId: 'receipt-date-desc',
				requiredFields: PURCHASE_ORDER_RECEIPTS_MAIN_REQUIRED_FIELDS,
				sortOrder: 0,
				filterRules: {
					// Effective `receiptDate >= today − 30d`. Locked +
					// hidden because the user's mental model for this tab
					// is "recent activity" — a custom date range belongs
					// in the All Receipts tab.
					receiptDateRange: {
						defaultValue: {
							startDate: 'TODAY-30',
							endDate: '',
						},
						locked: true,
						hidden: true,
					},
				},
			},
			{
				id: 'default-purchase-order-receipts-main-open-issues',
				cacheTTL: 60000,
				name: 'Open Issues',
				type: 'default',
				active: true,
				columns: PURCHASE_ORDER_RECEIPTS_MAIN_COLUMNS,
				description:
					'Receipts with at least one unresolved exception. Dock-side triage queue for receiving + the chase list for purchasing.',
				defaultSortId: 'receipt-date-asc',
				requiredFields: PURCHASE_ORDER_RECEIPTS_MAIN_REQUIRED_FIELDS,
				sortOrder: 1,
				filterRules: {
					receiptDispositionStatus: {
						defaultValue: [
							{
								id: PurchaseOrderReceiptDispositionStatus.HAS_OPEN_EXCEPTIONS,
								label: 'Open Issues',
							},
						],
						locked: true,
						hidden: true,
					},
				},
			},
			{
				id: 'default-purchase-order-receipts-main-all',
				cacheTTL: 600000,
				name: 'All Receipts',
				type: 'default',
				active: true,
				columns: PURCHASE_ORDER_RECEIPTS_MAIN_COLUMNS,
				description:
					'Every receipt in the system, no built-in filter. Use the sidebar to scope by vendor, warehouse, disposition status, or date range.',
				defaultSortId: 'receipt-date-desc',
				requiredFields: PURCHASE_ORDER_RECEIPTS_MAIN_REQUIRED_FIELDS,
				sortOrder: 2,
			},
		],
		paging: {
			defaultPageSize: 25,
			pageSizeOptions: [25, 50, 100],
		},
		filters: {
			activeFilters: [
				'receiptKeywords',
				'receiptDispositionStatus',
				'receiptVendor',
				'receiptWarehouse',
				'receiptDateRange',
			],
			defaultFilters: {},
		},
		objects: [
			{
				baseUrl: '/inventory/receipts/:id',
				objectLabel: 'Purchase Order Receipt',
				objectTable: 'purchase_order_receipt',
				selectedFields: [
					{ fieldName: '_id', fieldLabel: 'ID' },
					{ fieldName: 'externalId', fieldLabel: 'Receipt #' },
					{ fieldName: 'receiptDate', fieldLabel: 'Receipt Date' },
					{ fieldName: 'dispositionStatus', fieldLabel: 'Disposition' },
					{ fieldName: 'packingSlipNumber', fieldLabel: 'Packing Slip' },
				],
			},
		],
		tableSorts: [
			{
				id: 'receipt-date-desc',
				name: 'Receipt Date (newest)',
				fields: [
					{
						objectTable: 'purchase_order_receipt',
						direction: 'DESC',
						fieldName: 'receiptDate',
					},
				],
				description: 'Most recent receipts first',
			},
			{
				id: 'receipt-date-asc',
				name: 'Receipt Date (oldest)',
				fields: [
					{
						objectTable: 'purchase_order_receipt',
						direction: 'ASC',
						fieldName: 'receiptDate',
					},
				],
				description:
					'Oldest first — used by Open Issues to surface the most-overdue exceptions',
			},
			{
				id: 'receipt-number',
				name: 'Receipt Number',
				fields: [
					{
						objectTable: 'purchase_order_receipt',
						direction: 'ASC',
						fieldName: 'displayId',
					},
				],
				description: 'Sort by receipt number',
			},
			{
				id: 'vendor-name',
				name: 'Vendor',
				fields: [
					{
						objectTable: 'vendor',
						direction: 'ASC',
						fieldName: 'name',
					},
				],
				description: 'Sort alphabetically by vendor',
			},
		],
	};
